/**
 * "Built-in agent (bring your own key)" card (SPEC §7).
 *
 * The judge fallback and the video driver: a compact chat panel that drives
 * the SAME WebMCP tools through `runAgent()` with the user's own OpenAI or
 * Anthropic key. Keys and model names are remembered per provider in
 * `localStorage['bandmate:agent']` (try/catch around every access); a key is
 * only ever sent to the API of the provider it was entered for, and is never
 * logged. "Forget key" drops the selected provider's key.
 *
 *   const { el, dispose } = mountAgentPanel(drawerEl);
 *
 * Transcript rows: "You"/"Agent" text, tool calls as `→ add_chords {…}` and
 * results as `← ok · 4 chords` / `← error · message`. A `role=status` line
 * announces progress; the card carries `aria-busy` while a turn runs; Stop
 * aborts the in-flight run and hands focus back to the prompt. Enter sends,
 * Shift+Enter inserts a newline.
 *
 * Class prefix is `bm-agent-panel` (`bm-agent` alone is the app's teal text
 * utility in `styles/base.css`).
 */
import { h, setChildren } from '../ui/dom';
import { runAgent as defaultRunAgent, type AgentEvent, type ToolExecutor, type ToolsSource } from './loop';
import {
  PROVIDERS,
  isProviderName,
  type ChatMessage,
  type Provider,
  type ProviderName,
} from './providers';
import './panel.css';

/** localStorage key holding `{ provider, models: {…}, keys: {…} }` (see `AgentSettings`). */
export const AGENT_STORAGE_KEY = 'bandmate:agent';

/** Collaborators the panel uses; every one is replaceable (tests inject fakes). */
export interface AgentPanelDeps {
  runAgent: typeof defaultRunAgent;
  providers: Readonly<Record<ProviderName, Provider>>;
  /** Passed through to `runAgent` (defaults inside the loop when omitted). */
  toolsSource?: ToolsSource;
  executor?: ToolExecutor;
  /** Where settings persist; `null` disables persistence. Defaults to `localStorage`. */
  storage: Storage | null;
  maxRounds?: number;
}

/**
 * Persisted panel settings. Keys and models are kept per provider so that
 * switching providers never sends one provider's key (or model id) to the
 * other. The legacy flat shape `{ provider, model, apiKey }` is still read.
 */
export interface AgentSettings {
  provider: ProviderName;
  /** Model id per provider; absent → that provider's default. */
  models: Partial<Record<ProviderName, string>>;
  /** API key per provider; absent → none stored. */
  keys: Partial<Record<ProviderName, string>>;
}

/** Longest transcript row for tool calls / results, in characters. */
const ROW_MAX = 88;

const PROVIDER_LABEL: Readonly<Record<ProviderName, string>> = { openai: 'OpenAI', anthropic: 'Anthropic' };

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

/** Compact "→ name {args}" row text for a tool call. */
export function summarizeToolCall(name: string, args: Record<string, unknown>): string {
  let json = '{}';
  try {
    json = JSON.stringify(args) ?? '{}';
  } catch {
    json = '{…}';
  }
  return truncate(`→ ${name} ${json}`, ROW_MAX);
}

/** Compact "← ok · summary" / "← error · message" row text for a tool result. */
export function summarizeToolResult(resultText: string, ok: boolean): string {
  const text = (resultText ?? '').trim();
  const head = ok ? '← ok' : '← error';
  if (!text) return head;
  let detail = text;
  try {
    const parsed = JSON.parse(text) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const obj = parsed as Record<string, unknown>;
      const err = obj.error as { message?: unknown } | undefined;
      if (err && typeof err === 'object' && typeof err.message === 'string') {
        return truncate(`← error · ${err.message}`, ROW_MAX);
      }
      if (typeof obj.summary === 'string' && obj.summary.trim()) detail = obj.summary.trim();
      else if (typeof obj.message === 'string' && obj.message.trim()) detail = obj.message.trim();
      else detail = JSON.stringify(parsed);
    }
  } catch {
    /* plain text result */
  }
  return truncate(`${head} · ${detail.replace(/\s+/g, ' ')}`, ROW_MAX);
}

function readSettings(storage: Storage | null): AgentSettings | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(AGENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (!parsed || typeof parsed !== 'object' || !isProviderName(parsed.provider)) return null;
    const out: AgentSettings = { provider: parsed.provider, models: {}, keys: {} };
    const pick = (v: unknown): Partial<Record<ProviderName, string>> => {
      const o: Partial<Record<ProviderName, string>> = {};
      if (v && typeof v === 'object') {
        for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
          if (isProviderName(k) && typeof val === 'string' && val.trim()) o[k] = val.trim();
        }
      }
      return o;
    };
    out.models = pick(parsed.models);
    out.keys = pick(parsed.keys);
    // Legacy flat shape: one model + one key, belonging to the saved provider.
    if (typeof parsed.model === 'string' && parsed.model.trim() && out.models[out.provider] === undefined) {
      out.models[out.provider] = parsed.model.trim();
    }
    if (typeof parsed.apiKey === 'string' && parsed.apiKey.trim() && out.keys[out.provider] === undefined) {
      out.keys[out.provider] = parsed.apiKey.trim();
    }
    return out;
  } catch {
    return null;
  }
}

function writeSettings(storage: Storage | null, s: AgentSettings): void {
  if (!storage) return;
  try {
    storage.setItem(AGENT_STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* storage denied or full: the panel still works for this page load */
  }
}

let mountCounter = 0;

/**
 * Mount the agent card into `container`. Returns the card element and a
 * `dispose()` that aborts any running turn and removes the card.
 */
export function mountAgentPanel(
  container: HTMLElement,
  depsIn: Partial<AgentPanelDeps> = {},
): { el: HTMLElement; dispose(): void } {
  const deps: AgentPanelDeps = {
    runAgent: depsIn.runAgent ?? defaultRunAgent,
    providers: depsIn.providers ?? PROVIDERS,
    storage: depsIn.storage === undefined ? safeLocalStorage() : depsIn.storage,
    ...(depsIn.toolsSource ? { toolsSource: depsIn.toolsSource } : {}),
    ...(depsIn.executor ? { executor: depsIn.executor } : {}),
    ...(depsIn.maxRounds !== undefined ? { maxRounds: depsIn.maxRounds } : {}),
  };
  const uid = `bm-agent-${++mountCounter}`;
  const id = (s: string) => `${uid}-${s}`;

  // ----- state -----
  const saved = readSettings(deps.storage);
  let providerName: ProviderName = saved?.provider ?? 'openai';
  const models: Partial<Record<ProviderName, string>> = { ...(saved?.models ?? {}) };
  const keys: Partial<Record<ProviderName, string>> = { ...(saved?.keys ?? {}) };
  const modelFor = (name: ProviderName) => models[name] || deps.providers[name].defaultModel;
  let history: ChatMessage[] = [];
  let controller: AbortController | null = null;

  // ----- elements -----
  const providerSelect = h(
    'select',
    { id: id('provider'), name: 'provider', 'aria-describedby': id('hint') },
    ...(Object.keys(deps.providers) as ProviderName[]).map((name) =>
      h('option', { value: name, selected: name === providerName }, PROVIDER_LABEL[name]),
    ),
  );
  const modelInput = h('input', {
    id: id('model'),
    name: 'model',
    type: 'text',
    autocomplete: 'off',
    spellcheck: 'false',
    value: modelFor(providerName),
    placeholder: deps.providers[providerName].defaultModel,
  });
  const keyInput = h('input', {
    id: id('key'),
    name: 'apiKey',
    type: 'password',
    autocomplete: 'off',
    spellcheck: 'false',
    autocapitalize: 'off',
    value: keys[providerName] ?? '',
    placeholder: 'sk-…',
    'aria-describedby': id('hint'),
  });
  const forgetBtn = h('button', { type: 'button', class: 'bm-btn bm-btn--small bm-agent-panel__forget', 'data-action': 'forget' }, 'Forget key');
  const hint = h(
    'p',
    { id: id('hint'), class: 'bm-agent-panel__hint' },
    'Stored only in this browser (localStorage), one key per provider, and sent only to that provider.',
  );

  const transcript = h('ol', { class: 'bm-agent-panel__transcript', 'data-transcript': '', 'aria-label': 'Agent transcript' },
    h('li', { class: 'bm-agent-panel__empty' }, 'No messages yet. Try "Add lo-fi drums".'),
  );
  const prompt = h('textarea', {
    id: id('prompt'),
    name: 'prompt',
    rows: '2',
    placeholder: 'Ask the agent… (Enter to send, Shift+Enter for a new line)',
    'aria-describedby': id('status'),
  });
  const sendBtn = h('button', { type: 'button', class: 'bm-btn bm-btn--primary bm-agent-panel__send', 'data-action': 'send' }, 'Send');
  const stopBtn = h('button', { type: 'button', class: 'bm-btn bm-agent-panel__stop', 'data-action': 'stop', disabled: true }, 'Stop');
  const status = h('p', { id: id('status'), class: 'bm-agent-panel__status', role: 'status', 'aria-live': 'polite' }, '');

  const el = h(
    'section',
    { class: 'bm-panel bm-agent-panel', 'aria-labelledby': id('title'), 'aria-busy': 'false' },
    h(
      'div',
      { class: 'bm-agent-panel__head' },
      h('h2', { id: id('title') }, 'Built-in agent (bring your own key)'),
      h('p', { class: 'bm-agent-panel__explainer' }, 'Or use ChatGPT desktop / Chrome 149+ with WebMCP — this panel drives the same tools'),
    ),
    h(
      'div',
      { class: 'bm-agent-panel__settings' },
      h('div', { class: 'bm-agent-panel__field' }, h('label', { for: id('provider') }, 'Provider'), providerSelect),
      h('div', { class: 'bm-agent-panel__field' }, h('label', { for: id('model') }, 'Model'), modelInput),
      h('div', { class: 'bm-agent-panel__field bm-agent-panel__field--key' }, h('label', { for: id('key') }, 'API key'), keyInput, forgetBtn),
      hint,
    ),
    transcript,
    h(
      'div',
      { class: 'bm-agent-panel__compose' },
      h('label', { for: id('prompt'), class: 'bm-sr' }, 'Message for the agent'),
      prompt,
      sendBtn,
      stopBtn,
    ),
    status,
  );

  // ----- helpers -----
  const setStatus = (text: string) => {
    status.textContent = text;
  };
  /** Sync the fields into the per-provider tables and write them out. */
  const persist = () => {
    const model = modelInput.value.trim();
    if (model) models[providerName] = model;
    else delete models[providerName];
    const key = keyInput.value.trim();
    if (key) keys[providerName] = key;
    else delete keys[providerName];
    writeSettings(deps.storage, { provider: providerName, models: { ...models }, keys: { ...keys } });
  };
  const setBusy = (busy: boolean) => {
    el.setAttribute('aria-busy', busy ? 'true' : 'false');
    sendBtn.disabled = busy;
    stopBtn.disabled = !busy;
    providerSelect.disabled = busy;
    modelInput.readOnly = busy;
    keyInput.readOnly = busy;
  };
  let hasRows = false;
  const addRow = (kind: 'user' | 'assistant' | 'tool_call' | 'tool_result' | 'error', text: string, extra: Record<string, string> = {}) => {
    if (!hasRows) {
      hasRows = true;
      setChildren(transcript);
    }
    const who = kind === 'user' ? 'You' : kind === 'assistant' ? 'Agent' : kind === 'error' ? 'Error' : '';
    const li = h(
      'li',
      { class: 'bm-agent-panel__row', dataset: { kind, ...extra } },
      who ? h('span', { class: 'bm-agent-panel__who' }, who) : h('span', { class: 'bm-agent-panel__who', 'aria-hidden': 'true' }, ''),
      h('span', { class: 'bm-agent-panel__text', title: extra.title }, text),
    );
    transcript.append(li);
    transcript.scrollTop = transcript.scrollHeight;
    return li;
  };

  const onEvent = (e: AgentEvent) => {
    switch (e.type) {
      case 'thinking':
        setStatus(e.round > 1 ? `Thinking… (round ${e.round})` : 'Thinking…');
        break;
      case 'tool_call': {
        let full = '';
        try {
          full = JSON.stringify(e.arguments, null, 1) ?? '';
        } catch {
          full = '';
        }
        addRow('tool_call', summarizeToolCall(e.name, e.arguments), full ? { title: `${e.name} ${full}` } : {});
        setStatus(`Calling ${e.name}…`);
        break;
      }
      case 'tool_result':
        addRow('tool_result', summarizeToolResult(e.result, e.ok), { ok: String(e.ok), title: truncate(e.result, 600) });
        break;
      case 'assistant':
        if (e.text.trim()) addRow('assistant', e.text.trim());
        break;
      case 'error':
        addRow('error', e.message);
        setStatus(e.message);
        break;
    }
  };

  const send = async () => {
    if (controller) return;
    const text = prompt.value.trim();
    if (!text) {
      setStatus('Type a message first.');
      prompt.focus();
      return;
    }
    const apiKey = keyInput.value.trim();
    const provider = deps.providers[providerName];
    const label = PROVIDER_LABEL[providerName];
    if (!apiKey) {
      setStatus(`Add a ${label} API key first — it stays in this browser and is sent only to ${label}.`);
      keyInput.focus();
      return;
    }
    const model = modelInput.value.trim() || provider.defaultModel;
    modelInput.value = model;
    persist();

    controller = new AbortController();
    const { signal } = controller;
    setBusy(true);
    prompt.value = '';
    addRow('user', text);
    setStatus('Thinking…');

    try {
      const result = await deps.runAgent({
        provider,
        apiKey,
        model,
        userText: text,
        history,
        onEvent,
        signal,
        ...(deps.toolsSource ? { toolsSource: deps.toolsSource } : {}),
        ...(deps.executor ? { executor: deps.executor } : {}),
        ...(deps.maxRounds !== undefined ? { maxRounds: deps.maxRounds } : {}),
      });
      history = result.history;
      if (result.ok) setStatus('Done.');
      else if (result.aborted) setStatus('Stopped.');
      else setStatus(result.error ?? 'The agent stopped without an answer.');
    } catch (err) {
      // runAgent never rejects by contract; this guards injected replacements.
      const message = err instanceof Error ? err.message : String(err);
      onEvent({ type: 'error', message });
    } finally {
      controller = null;
      // Stop is about to be disabled: if focus is inside the card (on Stop, say),
      // hand it to the prompt. Never steal focus from the grid or piano — the
      // human may be playing while the agent works.
      const hadFocus = el.isConnected && el.contains(document.activeElement);
      setBusy(false);
      if (hadFocus) prompt.focus();
    }
  };

  // ----- wiring -----
  providerSelect.addEventListener('change', () => {
    const next = providerSelect.value;
    if (!isProviderName(next)) return;
    persist(); // capture the outgoing provider's fields
    providerName = next;
    modelInput.placeholder = deps.providers[next].defaultModel;
    modelInput.value = modelFor(next);
    keyInput.value = keys[next] ?? '';
    persist();
  });
  modelInput.addEventListener('input', persist);
  modelInput.addEventListener('change', () => {
    if (!modelInput.value.trim()) modelInput.value = deps.providers[providerName].defaultModel;
    persist();
  });
  keyInput.addEventListener('input', persist);
  keyInput.addEventListener('change', persist);
  forgetBtn.addEventListener('click', () => {
    keyInput.value = '';
    persist();
    setStatus(`Forgot the ${PROVIDER_LABEL[providerName]} key. It is no longer stored in this browser.`);
    keyInput.focus();
  });
  sendBtn.addEventListener('click', () => void send());
  stopBtn.addEventListener('click', () => {
    controller?.abort();
    setStatus('Stopping…');
  });
  prompt.addEventListener('keydown', (ev: KeyboardEvent) => {
    if (ev.key === 'Enter' && !ev.shiftKey && !ev.isComposing) {
      ev.preventDefault();
      void send();
    }
  });

  container.append(el);
  return {
    el,
    dispose() {
      controller?.abort();
      controller = null;
      el.remove();
    },
  };
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
